import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllCards } from '../Cards/actions';
import { selectAllCards } from '../Cards/selectors';
import CardFace from '../Card/CardFace';
import CardZoom, { useCardSteps } from '../CardZoom';
import { Muted, PlainButton, Select } from '../Controls';
import { Page, PageTitle, Panel } from '../Page';
import { EXPANSION_NAMES } from '../constants';
import { CardGrid, CardSlot } from '../DecksPage/styled';
import { filterCards } from './filterCards';
import { CardNumber, Filters, Search, Toggle } from './styled';

/** How many more cards show each time you scroll near the end. */
const PAGE = 60;

/**
 * Every card of every set, to look up: search, filter, and tap one to read it at full size. In set order, then by
 * number, each with its set and number; more load as you scroll.
 */
const CardLookup = () => {
  const dispatch = useDispatch();
  const cardsById = useSelector(selectAllCards);
  const [filters, setFilters] = useState({ query: '', set: '', kind: '', playableOnly: false });
  const [shown, setShown] = useState(PAGE);


  useEffect(() => {
    dispatch(getAllCards());
  }, [dispatch]);

  const found = useMemo(() => (cardsById ? filterCards(cardsById, filters) : []), [cardsById, filters]);
  // The full-size view steps through what the search found.
  const steps = useCardSteps(found);

  // More cards load as you scroll near the end of the list.
  const end = useRef(null);
  useEffect(() => {
    const marker = end.current;
    if (!marker) {
      return undefined;
    }
    const watcher = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShown(count => count + PAGE);
      }
    }, { rootMargin: '600px' });
    watcher.observe(marker);
    return () => watcher.disconnect();
  }, [found]);
  const change = (key) => (event) => {
    setFilters({ ...filters, [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value });
    setShown(PAGE);
  };

  return (
    <Page $wide>
      <PageTitle>Cards</PageTitle>
      <Panel>
        <Filters>
          <Search type="search" placeholder="Name or attack" value={filters.query} onChange={change('query')} aria-label="Search cards" />
          <Select value={filters.set} onChange={change('set')} aria-label="Set">
            <option value="">Every set</option>
            {Object.entries(EXPANSION_NAMES).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </Select>
          <Select value={filters.kind} onChange={change('kind')} aria-label="Kind">
            <option value="">Every kind</option>
            <option value="pokemon">Pokémon</option>
            <option value="trainer">Trainers</option>
            <option value="energy">Energy</option>
          </Select>
          <Toggle>
            <input type="checkbox" checked={filters.playableOnly} onChange={change('playableOnly')} />
            Playable only
          </Toggle>
        </Filters>
        {!cardsById && <Muted>Loading the cards…</Muted>}
        {cardsById && <Muted>{found.length} {found.length === 1 ? 'card' : 'cards'}</Muted>}
        <CardGrid style={{ marginTop: 10 }}>
          {found.slice(0, shown).map((card, i) => (
            <CardSlot key={card.get('id')}>
              <PlainButton onClick={() => steps.open(i)} aria-label={card.get('name')} style={{ opacity: card.get('enabled') === false ? 0.55 : 1 }}>
                <CardFace card={card} />
              </PlainButton>
              <CardNumber>{EXPANSION_NAMES[card.get('expansion_id')]} · {card.get('card_number')}</CardNumber>
            </CardSlot>
          ))}
        </CardGrid>
        {found.length > shown && <div ref={end} style={{ height: 1 }} />}
      </Panel>
      {steps.zoom && <CardZoom {...steps.zoom} />}
    </Page>
  );
};

export default CardLookup;
